import React, { useMemo } from 'react'
import { Row, Col } from 'reactstrap'

import ActivityHeatmap from './charts/ActivityHeatmap'
import ChartCard from './charts/ChartCard'
import DataTable from './charts/DataTable'
import HorizontalStackedBar from './charts/HorizontalStackedBar'
import Leaderboard from './charts/Leaderboard'
import StatTile, { StatTileRow } from './charts/StatTile'
import TrendLine from './charts/TrendLine'
import {
    buildActionMix,
    buildActionTrend,
    buildExtraTileValue,
    buildHeatmap,
    buildKpis,
    buildLeaderboard,
    buildLeaderboardTable,
    buildMixTableRows,
    buildUserKpis,
} from './activityReport'
import {
    actionMetaFor,
    entityColorFor,
    extraTilesFor,
    headlineActionsFor,
    seriesFor,
    seriesForChart,
    selfScopedChartsFor,
    selfScopedUserChartsFor,
} from './reportRegistry'

/**
 * Renders whatever charts a report's registry entry declares.
 *
 * This is the whole reason there is no per-report component: the three tab
 * components this replaces differed only in which actions they charted and what
 * they called them, both of which are now data.
 *
 * `layout` picks which of a report's two chart layouts to render:
 * `'module'` (the default) is the report's own subject -- incidents, claims,
 * whatever `report.charts` declares. `'users'` is who did the work --
 * `report.userCharts`, the same generic leaderboard/trend/heatmap layout on
 * every report. This is independent of `view`, which is chart-vs-table, not
 * module-vs-users.
 *
 * Colour separates created from edited throughout -- two measures of the same
 * record, and the only comparison on these charts that means anything.
 */
const ReportBody = ({ report, rows, view, layout = 'module', startDate, endDate, canViewAllUsers }) => {
    const actionMeta = useMemo(() => actionMetaFor(report), [report])

    // A viewer restricted to their own rows gets a layout that suits one
    // person: a one-bar "by staff member" chart, a one-row heatmap, and a
    // one-row leaderboard all say nothing. Derived from the report's own
    // charts (see `selfScopedChartsFor` / `selfScopedUserChartsFor`) so a
    // report that scopes its charts to avoid mixing record types keeps that
    // scoping in the self-scoped layout too.
    const charts = useMemo(() => {
        if (layout === 'users') {
            return canViewAllUsers ? report.userCharts : selfScopedUserChartsFor(report)
        }
        return canViewAllUsers ? report.charts : selfScopedChartsFor(report)
    }, [report, layout, canViewAllUsers])

    const actionValues = useMemo(() => report.actions.map((action) => action.value), [report])
    // The table view has no per-chart scoping -- it is one combined breakdown
    // of the whole report -- so it still uses the report-level series: one
    // series per action.
    const series = useMemo(() => seriesFor(report), [report])

    // Scopes the "created"/"edited" tiles to the report's headline actions
    // (e.g. incidents excludes parties, party people, party vehicles and
    // documents) -- see `headlineActionsFor` and `buildKpis`.
    const headlineActions = useMemo(() => headlineActionsFor(report), [report])
    const kpis = useMemo(
        () => buildKpis(rows, startDate, endDate, actionMeta, headlineActions),
        [rows, startDate, endDate, actionMeta, headlineActions]
    )
    // Extra tiles a report declares beyond the five generic ones above (e.g.
    // incidents' "Documents uploaded") -- see `extraTilesFor` /
    // `report.extraTiles` in reportRegistry.js. Empty for any report that
    // doesn't declare one, so this adds nothing to the other reports' rows.
    const extraTiles = useMemo(() => extraTilesFor(report), [report])
    const extraTileValues = useMemo(
        () => extraTiles.map((tile) => buildExtraTileValue(rows, actionMeta, tile.actions)),
        [extraTiles, rows, actionMeta]
    )
    // Feeds the "Most active user" tile below, on both layouts -- computed
    // unconditionally (rather than only for the Users layout) to keep the
    // hooks order stable and this component free of a conditional Hook call.
    const userKpis = useMemo(
        () => buildUserKpis(rows, startDate, endDate, actionMeta),
        [rows, startDate, endDate, actionMeta]
    )
    const entityColor = useMemo(() => entityColorFor(report), [report])
    const leaderboard = useMemo(
        () => buildLeaderboard(rows, actionMeta, entityColor),
        [rows, actionMeta, entityColor]
    )

    // Each chart may scope itself to a subset of the report's actions (see
    // `chart.actions` in the registry) so it never has to mix incompatible
    // record types -- an incidents chart and a documents chart, say -- into
    // one legend. `seriesForChart` resolves that scope per chart; an unscoped
    // chart still covers every action in the report, via the same `seriesFor`
    // the table view uses. A `leaderboard` chart is keyed by entity, not
    // action, so it takes its series from `buildLeaderboard` instead.
    const chartData = useMemo(
        () =>
            charts.map((chart) => {
                const chartActionValues = chart.actions || actionValues
                const chartSeries = chart.type === 'leaderboard' ? leaderboard.series : seriesForChart(report, chart)

                if (chart.type === 'trend') {
                    // A chart marked `colorBy: 'entity'` (the Users-view trend)
                    // needs its daily totals keyed by entity, not action, to
                    // match `chartSeries` above (see `seriesForChart`).
                    const seriesKeyFn =
                        chart.colorBy === 'entity' ? (row, meta) => meta?.entity : undefined
                    const data = buildActionTrend(
                        rows,
                        startDate,
                        endDate,
                        chartActionValues,
                        actionMeta,
                        seriesKeyFn
                    )
                    // Every day is emitted regardless of activity (see
                    // `buildActionTrend`), so an empty range is never a shorter
                    // array -- it is a full array of zeroes.
                    const isEmpty = data.every((record) => chartSeries.every((item) => !record[item.key]))
                    return { chart, data, isEmpty, series: chartSeries }
                }
                if (chart.type === 'heatmap') {
                    const data = buildHeatmap(rows, startDate, endDate, actionMeta)
                    return { chart, data, isEmpty: !data.grid.length, series: chartSeries }
                }
                if (chart.type === 'leaderboard') {
                    return { chart, data: leaderboard.data, isEmpty: !leaderboard.data.length, series: chartSeries }
                }
                const data = buildActionMix(rows, {
                    groupKeyFn: (row) => row[chart.groupBy],
                    actions: chartActionValues,
                    groupKey: chart.groupBy,
                    actionMeta,
                })
                return { chart, data, isEmpty: !data.length, series: chartSeries }
            }),
        [charts, rows, startDate, endDate, actionValues, actionMeta, report, leaderboard]
    )

    if (!rows.length) {
        return <p className="reports-empty">No activity in the selected date range.</p>
    }

    const leaderboardTable = buildLeaderboardTable(leaderboard.data, leaderboard.series)

    return (
        <>
            {!canViewAllUsers && (
                <p className="reports-scope-banner">
                    <i className="fa fa-user" aria-hidden="true"></i>
                    Showing your own activity &mdash; you don&rsquo;t have permission to see other users on this report.
                </p>
            )}

            <StatTileRow>
                <StatTile
                    label={`${report.noun} created`}
                    value={kpis.created}
                    testId="kpi-created"
                    caption="distinct records, not update events, in this range"
                />
                <StatTile
                    label={`${report.noun} edited`}
                    value={kpis.edited}
                    testId="kpi-edited"
                    // Plain statement of what the number is -- the "unions ids
                    // across days and users" mechanics stay a code comment on
                    // the maths itself (see `buildKpis` in activityReport.js),
                    // not on-screen.
                    caption="counted once, no matter how many times it was edited"
                />
                <StatTile
                    label="Total edit events"
                    value={kpis.editEvents}
                    testId="kpi-edit-events"
                    caption="every update logged, across every record type in this report"
                />
                <StatTile
                    label="Staff active"
                    value={kpis.staff}
                    testId="kpi-staff"
                    caption="people who logged an action here, not counting system automation"
                />
                <StatTile
                    label="Most active user"
                    value={userKpis.topUser}
                    caption={`${userKpis.topUserCount.toLocaleString()} actions`}
                    testId="kpi-most-active-user"
                />
                {extraTiles.map((tile, index) => (
                    <StatTile
                        key={tile.key || tile.label}
                        label={tile.label}
                        value={extraTileValues[index]}
                        caption={tile.caption}
                        testId={tile.testId || `kpi-extra-${tile.key || index}`}
                    />
                ))}
            </StatTileRow>

            {view === 'table' ? (
                layout === 'users' ? (
                    <DataTable
                        rows={leaderboardTable.rows}
                        series={leaderboardTable.columns}
                        rowHeader="Staff member"
                    />
                ) : (
                    <DataTable
                        rows={buildMixTableRows(
                            buildActionMix(rows, {
                                groupKeyFn: (row) => row.userName,
                                actions: actionValues,
                                groupKey: 'userName',
                                actionMeta,
                            }),
                            series,
                            'userName'
                        )}
                        series={series}
                        rowHeader="Staff member"
                    />
                )
            ) : (
                <Row>
                    {chartData.map(({ chart, data, isEmpty, series: chartSeries }) => (
                        <Col xs="12" md={chart.type === 'actionMix' ? '6' : '12'} key={chart.title}>
                            <ChartCard
                                title={chart.title}
                                subtitle={chart.caption}
                                isEmpty={isEmpty}
                                emptyMessage="No activity in the selected date range."
                            >
                                {chart.type === 'trend' && <TrendLine data={data} series={chartSeries} />}
                                {chart.type === 'actionMix' && (
                                    <HorizontalStackedBar data={data} series={chartSeries} yKey={chart.groupBy} />
                                )}
                                {chart.type === 'heatmap' && <ActivityHeatmap {...data} />}
                                {chart.type === 'leaderboard' && <Leaderboard data={data} series={chartSeries} />}
                            </ChartCard>
                        </Col>
                    ))}
                </Row>
            )}
        </>
    )
}

export default ReportBody
